import {
    Editor,
    Element as SlateElement,
    Node,
    Path,
    Point,
    Range,
    Text,
    Transforms,
} from "slate";
import {
    BulletedListElement,
    ListItemElement,
    ListType,
    NumberedListElement,
    ParagraphElement,
    isInlineElement,
} from "./slateTypes";

const MAX_LIST_DEPTH = 3;

type ListElement = BulletedListElement | NumberedListElement;

export const isListItemElement = (node: Node): node is ListItemElement =>
    SlateElement.isElement(node) && node.type === "list-item";

export const isListElement = (node: Node): node is ListElement =>
    SlateElement.isElement(node) &&
    (node.type === "bulleted-list" || node.type === "numbered-list");

export const isInlineChild = (node: Node): boolean =>
    Text.isText(node) ||
    (SlateElement.isElement(node) && isInlineElement(node));

export const emptyList = (format: ListType): ListElement =>
    format === "numbered-list"
        ? { type: "numbered-list", children: [] }
        : { type: "bulleted-list", children: [] };

export const getListItemEntry = (editor: Editor) =>
    Editor.above(editor, { match: isListItemElement });

const paragraphHasContent = (paragraph: ParagraphElement): boolean =>
    paragraph.children.some((child) => {
        if (Text.isText(child)) {
            return child.text.length > 0;
        }
        return isInlineElement(child);
    });

const listItemHasContent = (item: ListItemElement): boolean =>
    item.children.some((child) => {
        if (child.type === "paragraph") {
            return paragraphHasContent(child);
        }
        return child.children.some(listItemHasContent);
    });

export const isInListItem = (editor: Editor): boolean =>
    getListItemEntry(editor) != null;

export const isCurrentListItemEmpty = (editor: Editor): boolean => {
    const entry = getListItemEntry(editor);
    if (!entry) {
        return false;
    }
    return !listItemHasContent(entry[0]);
};

export const isAtStartOfListItem = (editor: Editor): boolean => {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) {
        return false;
    }
    const entry = getListItemEntry(editor);
    if (!entry) {
        return false;
    }
    return Point.equals(selection.anchor, Editor.start(editor, entry[1]));
};

const getListDepth = (editor: Editor, path: Path): number => {
    let depth = 0;
    for (const [ancestor] of Node.ancestors(editor, path)) {
        if (isListElement(ancestor)) {
            depth += 1;
        }
    }
    return depth;
};

export const wrapInList = (
    editor: Editor,
    format: ListType,
    options?: { checked: boolean },
): void => {
    const listItem: ListItemElement =
        options == null
            ? { type: "list-item", children: [] }
            : { type: "list-item", checked: options.checked, children: [] };
    Editor.withoutNormalizing(editor, () => {
        Transforms.wrapNodes(editor, listItem, {
            match: (node) =>
                SlateElement.isElement(node) && node.type === "paragraph",
        });
        Transforms.wrapNodes(editor, emptyList(format), {
            match: isListItemElement,
        });
    });
};

export const unwrapToParagraph = (editor: Editor): void => {
    Editor.withoutNormalizing(editor, () => {
        Transforms.unwrapNodes(editor, {
            match: isListElement,
            split: true,
            mode: "lowest",
        });
        Transforms.unwrapNodes(editor, {
            match: isListItemElement,
            split: true,
            mode: "lowest",
        });
    });
};

export const indentListItem = (editor: Editor): void => {
    const entry = getListItemEntry(editor);
    if (!entry) {
        return;
    }
    const [, path] = entry;
    if (path[path.length - 1] === 0) {
        return;
    }
    if (getListDepth(editor, path) >= MAX_LIST_DEPTH) {
        return;
    }

    const prevPath = Path.previous(path);
    const previous = Node.get(editor, prevPath);
    if (!isListItemElement(previous)) {
        return;
    }

    const parentList = Node.parent(editor, path);
    if (!isListElement(parentList)) {
        return;
    }

    const lastChildIndex = previous.children.length - 1;
    const lastChild = previous.children[lastChildIndex];

    Editor.withoutNormalizing(editor, () => {
        if (isListElement(lastChild) && lastChild.type === parentList.type) {
            Transforms.moveNodes(editor, {
                at: path,
                to: [...prevPath, lastChildIndex, lastChild.children.length],
            });
            return;
        }
        Transforms.wrapNodes(editor, emptyList(parentList.type), { at: path });
        Transforms.moveNodes(editor, {
            at: path,
            to: [...prevPath, previous.children.length],
        });
    });
};

export const outdentListItem = (editor: Editor): void => {
    const entry = getListItemEntry(editor);
    if (!entry) {
        return;
    }
    const [, path] = entry;
    const listPath = Path.parent(path);
    const listParent = Node.parent(editor, listPath);

    if (isListItemElement(listParent)) {
        Transforms.moveNodes(editor, {
            at: path,
            to: Path.next(Path.parent(listPath)),
        });
        return;
    }

    unwrapToParagraph(editor);
};

export const unwrapCurrentListItem = (editor: Editor): void => {
    const entry = getListItemEntry(editor);
    if (!entry) {
        return;
    }
    const listParent = Node.parent(editor, Path.parent(entry[1]));
    if (isListItemElement(listParent)) {
        outdentListItem(editor);
        return;
    }
    unwrapToParagraph(editor);
};
